/**
 * Scenarios (spec §15): up to 10 named plan variants, compare 3 side by side
 * across all assumption profiles, overlay chart, starter templates, and the
 * diff view showing exactly which inputs differ.
 */
import { useMemo, useState } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import type { Plan } from '../model/types';
import { runProjection } from '../engine/projection';
import { MAX_COMPARE, MAX_SCENARIOS, newScenarioId, type Scenario } from '../scenarios/scenario';
import { SCENARIO_TEMPLATES } from '../scenarios/templates';
import { diffPlans, fmtDiffValue } from '../scenarios/diff';
import { fmtCompactUsd, fmtUsd } from './format';

interface Props {
  plan: Plan;
  scenarios: Scenario[];
  onPlanChange: (p: Plan) => void;
  onScenariosChange: (s: Scenario[]) => void;
  todaysDollars: boolean;
}

const PROFILE_LABELS = ['optimistic', 'average', 'pessimistic'] as const;

interface ScenarioMetrics {
  endingByProfile: Record<string, number>; // today's $, per profile
  lifetimeTax: number; // active profile
  peakNetWorth: number;
  lastsUntil: number | 'end';
  series: { year: number; value: number }[];
}

function metricsFor(scnPlan: Plan): ScenarioMetrics {
  const endingByProfile: Record<string, number> = {};
  let active: ReturnType<typeof runProjection> | null = null;
  for (const label of PROFILE_LABELS) {
    if (!scnPlan.assumptionProfiles.some((p) => p.label === label)) continue;
    const variant: Plan = structuredClone(scnPlan);
    variant.activeAssumptionLabel = label;
    const r = runProjection(variant, 2026);
    const last = r.snapshots[r.snapshots.length - 1]!;
    endingByProfile[label] = last.netWorth / last.cumulativeInflationFactor;
    if (label === scnPlan.activeAssumptionLabel) active = r;
  }
  active ??= runProjection(scnPlan, 2026);

  const series = active.snapshots.map((s) => ({
    year: s.year,
    value: Math.round(s.netWorth / s.cumulativeInflationFactor),
  }));
  return {
    endingByProfile,
    lifetimeTax: active.snapshots.reduce((t, s) => t + s.totalTaxLiability, 0),
    peakNetWorth: Math.max(...series.map((s) => s.value)),
    lastsUntil: active.firstDepletionYear ?? 'end',
    series,
  };
}

const SERIES_VARS = ['var(--series-1)', 'var(--series-2)', 'var(--series-3, #7c9a5e)'];

/**
 * A name not already taken, suffixed if need be. Numbering by `length + 1`
 * collides the moment anything is deleted — save, save, delete the first, save
 * again, and you have two "Scenario 2". Adding the same template twice collided
 * the same way.
 */
function uniqueScenarioName(base: string, taken: Scenario[]): string {
  const used = new Set(taken.map((s) => s.name));
  if (!used.has(base)) return base;
  for (let n = 2; ; n++) {
    const candidate = `${base} (${n})`;
    if (!used.has(candidate)) return candidate;
  }
}

/** The next free "Scenario N", counting past whatever survives a delete. */
function nextScenarioName(taken: Scenario[]): string {
  const used = new Set(taken.map((s) => s.name));
  for (let n = 1; ; n++) {
    if (!used.has(`Scenario ${n}`)) return `Scenario ${n}`;
  }
}

export function ScenariosCard({ plan, scenarios, onPlanChange, onScenariosChange }: Props) {
  const [compareIds, setCompareIds] = useState<string[]>([]);
  const [showDiff, setShowDiff] = useState(false);

  const compared = scenarios.filter((s) => compareIds.includes(s.id));
  const comparedMetrics = useMemo(
    () => compared.map((s) => ({ scenario: s, metrics: metricsFor(s.plan) })),
    [compared],
  );

  const overlayData = useMemo(() => {
    if (comparedMetrics.length === 0) return [];
    const byYear = new Map<number, Record<string, number>>();
    for (const { scenario, metrics } of comparedMetrics) {
      for (const pt of metrics.series) {
        const row = byYear.get(pt.year) ?? {};
        // Keyed by ID, never by name: two scenarios the user happened to name
        // the same would otherwise overwrite each other here and be drawn as a
        // single line — a comparison quietly showing the wrong answer.
        row[scenario.id] = pt.value;
        byYear.set(pt.year, row);
      }
    }
    return [...byYear.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([year, values]) => ({ year, ...values }));
  }, [comparedMetrics]);

  function saveCurrent() {
    if (scenarios.length >= MAX_SCENARIOS) return;
    onScenariosChange([
      ...scenarios,
      { id: newScenarioId(), name: nextScenarioName(scenarios), plan: structuredClone(plan) },
    ]);
  }

  function addTemplate(key: string) {
    if (scenarios.length >= MAX_SCENARIOS) return;
    const tpl = SCENARIO_TEMPLATES.find((t) => t.key === key)!;
    onScenariosChange([
      ...scenarios,
      { id: newScenarioId(), name: uniqueScenarioName(tpl.label, scenarios), plan: tpl.apply(plan) },
    ]);
  }

  function toggleCompare(id: string) {
    setCompareIds((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : cur.length < MAX_COMPARE ? [...cur, id] : cur,
    );
  }

  return (
    <section className="card" aria-label="Scenarios">
      <h2 className="section-title">
        Scenarios
        <span className="note">
          save variations of your plan, compare up to {MAX_COMPARE} side by side — they travel
          with share links and exports
        </span>
      </h2>

      <div className="add-row" style={{ marginBottom: 'var(--space-3)' }}>
        <button className="btn" onClick={saveCurrent} disabled={scenarios.length >= MAX_SCENARIOS}>
          + Save current plan as scenario
        </button>
        <select
          value=""
          aria-label="Add from template"
          onChange={(e) => { if (e.target.value) addTemplate(e.target.value); e.target.value = ''; }}
          disabled={scenarios.length >= MAX_SCENARIOS}
        >
          <option value="">Add from template…</option>
          {SCENARIO_TEMPLATES.map((t) => (
            <option key={t.key} value={t.key}>{t.label}</option>
          ))}
        </select>
        {scenarios.length >= MAX_SCENARIOS && <span className="hint">limit of {MAX_SCENARIOS} reached</span>}
      </div>

      {scenarios.length === 0 && (
        <p className="hint">
          No scenarios yet. Save the current plan, or start from a template — "Retire 2 years
          earlier" and "Delay Social Security to 70" are the classics.
        </p>
      )}

      {scenarios.map((s) => (
        <div key={s.id} className="add-row" style={{ borderBottom: '1px solid var(--hairline)', padding: '6px 0' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="checkbox"
              checked={compareIds.includes(s.id)}
              disabled={!compareIds.includes(s.id) && compareIds.length >= MAX_COMPARE}
              onChange={() => toggleCompare(s.id)}
            />
            compare
          </label>
          <input
            value={s.name}
            aria-label="Scenario name"
            style={{ flex: 1, minWidth: 140 }}
            onChange={(e) =>
              onScenariosChange(scenarios.map((x) => (x.id === s.id ? { ...x, name: e.target.value } : x)))
            }
          />
          <button className="btn" onClick={() => onPlanChange(structuredClone(s.plan))}>
            Load into editor
          </button>
          <button className="btn" onClick={() =>
            onScenariosChange(scenarios.map((x) => (x.id === s.id ? { ...x, plan: structuredClone(plan) } : x)))
          }>
            Overwrite with current
          </button>
          <button className="btn danger" onClick={() => {
            onScenariosChange(scenarios.filter((x) => x.id !== s.id));
            setCompareIds((cur) => cur.filter((x) => x !== s.id));
          }}>
            Delete
          </button>
        </div>
      ))}

      {comparedMetrics.length >= 2 && (
        <>
          <h3 style={{ margin: 'var(--space-5) 0 var(--space-2)', fontSize: 'var(--fs-body)' }}>
            Side by side
          </h3>
          <div className="heatmap-wrap">
            <table className="roth-table">
              <thead>
                <tr>
                  <th>Scenario</th>
                  <th>Ending (optimistic)</th>
                  <th>Ending (average)</th>
                  <th>Ending (pessimistic)</th>
                  <th>Peak net worth</th>
                  <th>Lifetime taxes</th>
                  <th>Money lasts</th>
                </tr>
              </thead>
              <tbody>
                {comparedMetrics.map(({ scenario, metrics }, i) => (
                  <tr key={scenario.id}>
                    <td>
                      <span style={{ color: SERIES_VARS[i], fontWeight: 700 }}>■ </span>
                      {scenario.name}
                    </td>
                    {PROFILE_LABELS.map((label) => (
                      <td key={label}>
                        {metrics.endingByProfile[label] != null
                          ? fmtCompactUsd(metrics.endingByProfile[label]!)
                          : '—'}
                      </td>
                    ))}
                    <td>{fmtCompactUsd(metrics.peakNetWorth)}</td>
                    <td>{fmtCompactUsd(metrics.lifetimeTax)}</td>
                    <td>{metrics.lastsUntil === 'end' ? 'to the end ✓' : `runs out ${metrics.lastsUntil}`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ width: '100%', height: 240, marginTop: 'var(--space-4)' }}>
            <ResponsiveContainer>
              <LineChart data={overlayData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid stroke="var(--grid)" vertical={false} />
                <XAxis dataKey="year" stroke="var(--axis)" tickLine={false}
                  tick={{ fill: 'var(--ink-muted)', fontSize: 12 }} tickMargin={8} minTickGap={28} />
                <YAxis stroke="var(--axis)" tickLine={false} axisLine={false}
                  tick={{ fill: 'var(--ink-muted)', fontSize: 12 }}
                  tickFormatter={(v: number) => fmtCompactUsd(v)} width={64} />
                <Tooltip
                  contentStyle={{
                    background: 'var(--surface-raised)', border: '1px solid var(--border)',
                    borderRadius: 8, fontSize: 13, color: 'var(--ink)',
                  }}
                  formatter={(value) => fmtUsd(Number(value))}
                  labelStyle={{ color: 'var(--ink-secondary)', fontWeight: 600 }}
                />
                <Legend wrapperStyle={{ fontSize: 13, color: 'var(--ink-secondary)' }} iconType="plainline" />
                {comparedMetrics.map(({ scenario }, i) => (
                  // dataKey is the id; `name` is what the legend and tooltip show.
                  <Line key={scenario.id} dataKey={scenario.id} name={scenario.name}
                    type="monotone" stroke={SERIES_VARS[i]} strokeWidth={2} dot={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="hint">Net worth in today's dollars, each scenario under its own active profile.</p>

          <button className="btn" style={{ marginTop: 'var(--space-3)' }}
            onClick={() => setShowDiff(!showDiff)}>
            {showDiff ? 'Hide input differences' : 'What actually differs between these?'}
          </button>
          {showDiff && (
            <div style={{ marginTop: 'var(--space-3)' }}>
              {compared.slice(1).map((s) => {
                const diffs = diffPlans(compared[0]!.plan, s.plan);
                return (
                  <div key={s.id} style={{ marginBottom: 'var(--space-3)' }}>
                    <h4 style={{ margin: '0 0 6px', fontSize: 'var(--fs-small)', color: 'var(--ink-secondary)' }}>
                      "{compared[0]!.name}" vs "{s.name}" — {diffs.length}{' '}
                      {diffs.length === 1 ? 'difference' : 'differences'}
                    </h4>
                    {diffs.length === 0 ? (
                      <p className="hint">Identical inputs — any outcome difference is the assumption profile.</p>
                    ) : (
                      <table className="roth-table">
                        <tbody>
                          {diffs.slice(0, 30).map((d) => (
                            <tr key={d.path}>
                              <td style={{ fontFamily: 'monospace', fontSize: 'var(--fs-micro)' }}>{d.path}</td>
                              <td>{fmtDiffValue(d.a)}</td>
                              <td>→ {fmtDiffValue(d.b)}</td>
                            </tr>
                          ))}
                          {diffs.length > 30 && (
                            <tr><td colSpan={3} className="hint">…and {diffs.length - 30} more</td></tr>
                          )}
                        </tbody>
                      </table>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </section>
  );
}
